import React, { useState } from 'react';
import { FileText, Download, Star, Shield, DollarSign, TrendingUp, Mail, User, X } from 'lucide-react';

const AnnuityGuide: React.FC = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    setShowPopup(false);
  };

  // Function to handle closing the popup
  const handleClosePopup = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPopup(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
                Discover 5 Proven Strategies to Generate
                <span className="block text-primary">Guaranteed Retirement Income</span>
              </h1>
              <p className="mt-4 text-lg text-gray-500">
                Download our comprehensive guide to learn how to secure your financial future with guaranteed income strategies.
              </p>
              <div className="mt-8">
                <button
                  onClick={() => setShowPopup(true)}
                  className="btn btn-primary px-8 py-3 text-lg"
                >
                  Download Free Guide
                </button>
              </div>
            </div>
            <div className="mt-12 lg:mt-0">
              <div className="bg-primary/5 rounded-lg p-8">
                <h3 className="text-xl font-semibold mb-4">What You'll Learn:</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <Shield className="h-6 w-6 text-primary mr-3 flex-shrink-0" />
                    <span>How to protect your retirement savings from market volatility</span>
                  </li>
                  <li className="flex items-start">
                    <DollarSign className="h-6 w-6 text-primary mr-3 flex-shrink-0" />
                    <span>Strategies to maximize your Social Security benefits</span>
                  </li>
                  <li className="flex items-start">
                    <TrendingUp className="h-6 w-6 text-primary mr-3 flex-shrink-0" />
                    <span>Ways to create predictable retirement income</span>
                  </li>
                  <li className="flex items-start">
                    <Star className="h-6 w-6 text-primary mr-3 flex-shrink-0" />
                    <span>Tips for choosing the right annuity for your needs</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Trusted by Thousands</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex text-secondary mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-5 w-5" fill="currentColor" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4">
                  "This guide helped me understand exactly how to secure my retirement income. The strategies are clear and actionable."
                </p>
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">John Smith</p>
                    <p className="text-sm text-gray-500">Retired Professional</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Popup Form */}
      {showPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="bg-white rounded-lg p-8 max-w-md w-full mx-4" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-primary mr-3" />
                <h2 className="text-2xl font-bold">Download Your Free Guide</h2>
              </div>
              <button 
                onClick={handleClosePopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    required
                    className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    required
                    className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-4">
                By downloading this guide, you agree to receive occasional updates and marketing messages from us. You can unsubscribe at any time.
              </p>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleClosePopup}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <Download className="h-4 w-4 mr-2" />
                  Download Now
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnuityGuide;